import { useCallback } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import {
  API_MAX_PAGE_SIZE,
  DEFAULT_WINDOW_SIZE,
  INITIAL_PAGE_SIZE,
} from './constants'

// The URL shape every list/table page shares. The DataDropdown window is
// stored as explicit `from`/`to` (both 1-indexed, inclusive); `size`/`page`
// drive the bottom bar's sub-pagination within that window. Storing the
// window verbatim keeps deep links meaningful across reloads. `q` mirrors the
// list endpoint's query param so copy-pasted URLs stay meaningful too.
export const QUICK_CREATE_SEARCH_VALUES = ['quote', 'order'] as const
export type QuickCreateSearchValue = (typeof QUICK_CREATE_SEARCH_VALUES)[number]

/**
 * The open phone-panel conversation, kept in the URL so a reload reopens the
 * same chat. The panel is global, so every route's `validateSearch` should
 * preserve these via `pickConversationSearch` (routes with no validator pass
 * them through automatically).
 */
export interface ConversationSearch {
  /** Conversation id (opened from a thread row). */
  chat?: string
  /** Counterparty E.164 (opened by number from Calls/Missed/Favorites). */
  chatTo?: string
  /** Timeline item the thread is anchored on (a search hit) — loads the page
   *  around that message instead of the newest one. */
  chatAt?: string
  /** Activity-list direction filter (`inbound` / `outbound`). */
  dir?: string
  /** Activity-list agent scope (`all` or an agent id). */
  agent?: string
  /**
   * Activity-list date bounds as `YYYY-MM-DD`. Deliberately *not* `from`/`to`:
   * those are the numeric table-window params and would collide on list routes.
   */
  fromDate?: string
  toDate?: string
  /** Activity-list status chips, comma-joined (`unread,failed`). */
  status?: string
}

/** The global phone-panel's URL params — preserved on every route's search. */
const CONVERSATION_KEYS = [
  'chat',
  'chatTo',
  'chatAt',
  'dir',
  'agent',
  'fromDate',
  'toDate',
  'status',
] as const

export function pickConversationSearch(
  raw: Record<string, unknown>,
): ConversationSearch {
  const out: ConversationSearch = {}
  for (const key of CONVERSATION_KEYS) {
    const value = raw[key]
    if (typeof value === 'string' && value.length > 0) out[key] = value
  }
  return out
}

export interface TableSearchParams extends ConversationSearch {
  /** Window bounds (1-indexed, inclusive) the DataDropdown fetches. */
  from?: number
  to?: number
  /** "Show per page": how the bottom bar splits the fetched window. */
  size?: number
  /** Sub-page within the window the bottom bar is on (1-indexed). */
  page?: number
  q?: string
  create?: QuickCreateSearchValue
  /** Open detail-sidebar shipment id; keeps the preview panel deep-linkable. */
  preview?: string
}

function clampInt(raw: unknown, fallback: number, min: number): number {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number(raw)
        : Number.NaN
  if (!Number.isFinite(n) || n < min) return fallback
  return Math.floor(n)
}

// The numeric URL params and the fallback each takes when absent/invalid.
// Looping over this keeps `validateTableSearch` under the complexity ceiling.
const NUMBER_PARAM_FALLBACKS: ReadonlyArray<
  [keyof Pick<TableSearchParams, 'from' | 'to' | 'size' | 'page'>, number]
> = [
  ['from', 1],
  ['to', DEFAULT_WINDOW_SIZE],
  ['size', INITIAL_PAGE_SIZE],
  ['page', 1],
]

/**
 * Parse an unknown search-param record into the shared table shape. Suitable
 * to pass as a TanStack Router `validateSearch` on any list route so the
 * route's search is typed and invalid values fall back to defaults instead
 * of breaking the query.
 */
export function validateTableSearch(
  raw: Record<string, unknown>,
): TableSearchParams {
  const out: TableSearchParams = {}
  for (const [key, fallback] of NUMBER_PARAM_FALLBACKS) {
    const value = raw[key]
    if (value !== undefined) out[key] = clampInt(value, fallback, 1)
  }
  const rawQ = raw['q']
  if (typeof rawQ === 'string' && rawQ.length > 0) {
    out.q = rawQ
  }
  const rawCreate = raw['create']
  if (
    typeof rawCreate === 'string' &&
    (QUICK_CREATE_SEARCH_VALUES as readonly string[]).includes(rawCreate)
  ) {
    out.create = rawCreate as QuickCreateSearchValue
  }
  const rawPreview = raw['preview']
  if (typeof rawPreview === 'string' && rawPreview.length > 0) {
    out.preview = rawPreview
  }
  return { ...out, ...pickConversationSearch(raw) }
}

export interface TableSearchPatch {
  from?: number
  to?: number
  size?: number
  page?: number
  search?: string
}

export interface UseTableSearchParamsResult {
  from: number
  to: number
  /** "Show per page" for the bottom bar (defaults to INITIAL_PAGE_SIZE). */
  size: number
  /** Current sub-page within the window (defaults to 1). */
  page: number
  search: string
  /** Move the window. Resets the sub-page to 1. */
  setRange: (from: number, to: number) => void
  /** Change "show per page". Resets the sub-page to 1. */
  setSize: (size: number) => void
  /** Move to a sub-page within the current window. */
  setPage: (page: number) => void
  setSearch: (search: string) => void
  update: (patch: TableSearchPatch) => void
}

type LooseSearchRecord = Record<string, unknown>

const DEFAULT_FROM = 1
const DEFAULT_TO = DEFAULT_WINDOW_SIZE
const DEFAULT_SIZE = INITIAL_PAGE_SIZE
const DEFAULT_PAGE = 1

// Numeric patch keys paired with the default that collapses them out of the
// URL. Looping keeps the `update` writer under the complexity ceiling.
const NUMBER_PATCH_DEFAULTS: ReadonlyArray<
  [keyof Pick<TableSearchPatch, 'from' | 'to' | 'size' | 'page'>, number]
> = [
  ['from', DEFAULT_FROM],
  ['to', DEFAULT_TO],
  ['size', DEFAULT_SIZE],
  ['page', DEFAULT_PAGE],
]

/**
 * Read and write the shared table search params on the current route. Values
 * matching the defaults are collapsed back to `undefined` on write so the
 * URL stays clean (no `?from=1&to=25` noise when the user hasn't moved).
 */
export function useTableSearchParams(): UseTableSearchParamsResult {
  const raw: LooseSearchRecord = useSearch({ strict: false })
  const navigate = useNavigate()

  const from = clampInt(raw['from'], DEFAULT_FROM, 1)
  // Free window: only the visible sub-page is fetched, so `to` isn't capped to
  // one API page — just kept at or above `from`.
  const to = Math.max(from, clampInt(raw['to'], DEFAULT_TO, 1))
  // `size` is fetched directly as the API page size, so it can't exceed the cap.
  const size = Math.min(
    API_MAX_PAGE_SIZE,
    clampInt(raw['size'], DEFAULT_SIZE, 1),
  )
  const page = clampInt(raw['page'], DEFAULT_PAGE, 1)
  const rawQ = raw['q']
  const search = typeof rawQ === 'string' ? rawQ : ''

  const update = useCallback(
    (patch: TableSearchPatch) => {
      const searchUpdater = (prev: LooseSearchRecord): LooseSearchRecord => {
        const next: LooseSearchRecord = { ...prev }
        // A value matching its default collapses to `undefined` so the URL
        // stays clean (no `?from=1&to=100&page=1` noise on a fresh table).
        for (const [key, dflt] of NUMBER_PATCH_DEFAULTS) {
          const value = patch[key]
          if (value !== undefined)
            next[key] = value === dflt ? undefined : value
        }
        if (patch.search !== undefined) {
          const trimmed = patch.search.trim()
          next['q'] = trimmed.length > 0 ? trimmed : undefined
        }
        return next
      }
      // The hook is route-agnostic, so navigate's typed search shape is
      // unknowable here; cast through `never` keeps the call site honest
      // without leaking `any`.
      navigate({
        search: searchUpdater as never,
        // Debounced search updates on every keystroke would otherwise pile
        // up in browser history; this keeps Back/Forward tied to real moves.
        replace: true,
      })
    },
    [navigate],
  )

  const setRange = useCallback(
    (nextFrom: number, nextTo: number) => {
      // Free range — callers that know the dataset total (the DataDropdown,
      // the window arrows) clamp `to` to it first; here we only enforce the
      // structural invariant `1 <= from <= to`. Only the visible sub-page is
      // ever fetched, so the window has no size cap. Moving it restarts the
      // bottom bar at its first sub-page.
      const safeFrom = Math.max(1, nextFrom)
      update({
        from: safeFrom,
        to: Math.max(safeFrom, nextTo),
        page: DEFAULT_PAGE,
      })
    },
    [update],
  )
  const setSize = useCallback(
    (nextSize: number) => update({ size: nextSize, page: DEFAULT_PAGE }),
    [update],
  )
  const setPage = useCallback(
    (nextPage: number) => update({ page: nextPage }),
    [update],
  )
  const setSearch = useCallback(
    (s: string) =>
      update({
        from: DEFAULT_FROM,
        to: DEFAULT_TO,
        page: DEFAULT_PAGE,
        search: s,
      }),
    [update],
  )

  return {
    from,
    to,
    size,
    page,
    search,
    setRange,
    setSize,
    setPage,
    setSearch,
    update,
  }
}
